import React, { useState, useRef, useEffect } from 'react';
import './chatbot.css';

const ChatBot = () => {
    const [isOpen, setIsOpen] = useState(false);
    const [showChat, setShowChat] = useState(false);
    const [messages, setMessages] = useState([
        {
            id: 1,
            text: "I'm an AI assistant, specifically trained for Smoothcomp 💪\n\nLet me try and assist you for quick answers! ⚡ If needed I will transfer you to our human support team 🔥",
            isUser: false,
            timestamp: new Date()
        }
    ]);
    const [inputText, setInputText] = useState('');
    const messagesEndRef = useRef(null);
    const dropdownRef = useRef(null);

    const scrollToBottom = () => {
        messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
    };

    useEffect(() => {
        scrollToBottom();
    }, [messages]);

    // Close dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setIsOpen(false);
                setShowChat(false);
            }
        };

        if (isOpen) {
            document.addEventListener('mousedown', handleClickOutside);
        }

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [isOpen]);

    const sendMessage = () => {
        if (inputText.trim() === '') return;

        const userMessage = {
            id: Date.now(),
            text: inputText,
            isUser: true,
            timestamp: new Date()
        };

        setMessages(prev => [...prev, userMessage]);
        setInputText('');

        setTimeout(() => {
            const botResponse = {
                id: Date.now() + 1,
                text: "Thanks for your message! I'm here to help. How can I assist you further?",
                isUser: false,
                timestamp: new Date()
            };
            setMessages(prev => [...prev, botResponse]);
        }, 1000);
    };

    const handleKeyPress = (e) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            sendMessage();
        }
    };

    const handleChatWithUs = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setShowChat(true);
    };

    const handleBackToWelcome = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setShowChat(false);
    };

    return (
        <div className="navbar-item dropdown" ref={dropdownRef}>
            <a
                href="#/"
                className="navbar-link dropdown-toggle"
                onClick={(e) => {
                    e.preventDefault();
                    setIsOpen(!isOpen);
                    if (!isOpen) {
                        setShowChat(false);
                    }
                }}
                data-bs-toggle="dropdown"
            >
                <ChatIcon />
            </a>

            <div
                className={`dropdown-menu dropdown-menu-end chatbot-dropdown ${isOpen ? 'show' : ''}`}
                onClick={(e) => e.stopPropagation()}
            >
                {!showChat ? (
                    <>
                        {/* Welcome Header */}
                        <div className="chatbot-welcome-header">
                            <div className="chatbot-welcome-title">Hi there! 👋</div>
                            <div className="chatbot-welcome-subtitle">Welcome back!</div>
                        </div>

                        {/* Assistant Card */}
                        <div className="chatbot-content-area">
                            <div className="chatbot-assistant-card">
                                <div className="chatbot-assistant-header">
                                    <div className="chatbot-bot-icon-container">
                                        <BotIcon />
                                    </div>
                                    <div>
                                        <div className="chatbot-assistant-title">Smooth Operator</div>
                                        <div className="chatbot-assistant-subtitle">Need assistance?</div>
                                    </div>
                                </div>

                                {/* Chat with us button */}
                                <button
                                    onClick={handleChatWithUs}
                                    className="chatbot-chat-button"
                                >
                                    Chat with us
                                </button>
                            </div>

                            {/* Menu Items */}
                            <div className="chatbot-menu-items">
                                {[
                                    'Knowledge Base for organizers',
                                    'Knowledge Base for athletes',
                                    'Become an organizer',
                                    'By using this chatbot you agree to our terms of service'
                                ].map((item, idx) => (
                                    <div
                                        key={idx}
                                        className="chatbot-menu-item"
                                        onClick={(e) => e.preventDefault()}
                                    >
                                        <span>{item}</span>
                                        <DotsIcon />
                                    </div>
                                ))}
                            </div>
                        </div>
                    </>
                ) : (
                    <>
                        {/* Chat Header */}
                        <div className="chatbot-chat-header">
                            <div className="chatbot-header-left">
                                <button
                                    onClick={handleBackToWelcome}
                                    className="chatbot-back-button"
                                >
                                    ←
                                </button>
                                <BotIcon />
                                <span className="chatbot-operator-title">Smooth Operator</span>
                            </div>
                            <button
                                type="button"
                                className="chatbot-close-button"
                                onClick={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    setIsOpen(false);
                                    setShowChat(false);
                                }}
                            >
                                ×
                            </button>
                        </div>

                        {/* Messages Area */}
                        <div className="chatbot-messages-area">
                            {messages.map((message) => (
                                <div
                                    key={message.id}
                                    className={`chatbot-message-container ${message.isUser ? 'chatbot-message-user' : 'chatbot-message-bot'}`}
                                >
                                    {!message.isUser && <BotIcon />}
                                    <div
                                        className={`chatbot-message-bubble ${message.isUser ? 'chatbot-message-user-bubble' : 'chatbot-message-bot-bubble'}`}
                                    >
                                        <div className="chatbot-message-text">{message.text}</div>
                                        <div
                                            className={`chatbot-message-timestamp ${message.isUser ? 'chatbot-message-user-timestamp' : 'chatbot-message-bot-timestamp'}`}
                                        >
                                            {message.timestamp.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                                        </div>
                                    </div>
                                </div>
                            ))}
                            <div ref={messagesEndRef} />
                        </div>

                        {/* Input Area */}
                        <div className="chatbot-input-area">
                            <div className="chatbot-input-container">
                                <textarea
                                    className="chatbot-textarea"
                                    placeholder="Write a message..."
                                    value={inputText}
                                    onChange={(e) => setInputText(e.target.value)}
                                    onKeyPress={handleKeyPress}
                                    rows="1"
                                />
                                <button
                                    className={`chatbot-send-button ${inputText.trim() ? 'chatbot-send-button-active' : ''}`}
                                    onClick={(e) => {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        sendMessage();
                                    }}
                                    disabled={!inputText.trim()}
                                >
                                    <SendIcon />
                                </button>
                            </div>
                        </div>
                    </>
                )}
            </div>
        </div>
    );
};

const BotIcon = () => {
    return (<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" className="bi bi-robot" viewBox="0 0 16 16">
        <path d="M6 12.5a.5.5 0 0 1 .5-.5h3a.5.5 0 0 1 0 1h-3a.5.5 0 0 1-.5-.5M3 8.062C3 6.76 4.235 5.765 5.53 5.886a26.6 26.6 0 0 0 4.94 0C11.765 5.765 13 6.76 13 8.062v1.157a.93.93 0 0 1-.765.935c-.845.147-2.34.346-4.235.346s-3.39-.2-4.235-.346A.93.93 0 0 1 3 9.219zm4.542-.827a.25.25 0 0 0-.217.068l-.92.9a25 25 0 0 1-1.871-.183.25.25 0 0 0-.068.495c.55.076 1.232.149 2.02.193a.25.25 0 0 0 .189-.071l.754-.736.847 1.71a.25.25 0 0 0 .404.062l.932-.97a25 25 0 0 0 1.922-.188.25.25 0 0 0-.068-.495c-.538.074-1.207.145-1.98.189a.25.25 0 0 0-.166.076l-.754.785-.842-1.7a.25.25 0 0 0-.182-.135" />
        <path d="M8.5 1.866a1 1 0 1 0-1 0V3h-2A4.5 4.5 0 0 0 1 7.5V8a1 1 0 0 0-1 1v2a1 1 0 0 0 1 1v1a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2v-1a1 1 0 0 0 1-1V9a1 1 0 0 0-1-1v-.5A4.5 4.5 0 0 0 10.5 3h-2zM14 7.5V13a1 1 0 0 1-1 1H3a1 1 0 0 1-1-1V7.5A3.5 3.5 0 0 1 5.5 4h5A3.5 3.5 0 0 1 14 7.5" />
    </svg>)
}

const ChatIcon = () => {
    return (<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" className="bi bi-chat-square-text-fill" viewBox="0 0 16 16">
        <path d="M0 2a2 2 0 0 1 2-2h12a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2h-2.5a1 1 0 0 0-.8.4l-1.9 2.533a1 1 0 0 1-1.6 0L5.3 12.4a1 1 0 0 0-.8-.4H2a2 2 0 0 1-2-2zm3.5 1a.5.5 0 0 0 0 1h9a.5.5 0 0 0 0-1zm0 2.5a.5.5 0 0 0 0 1h9a.5.5 0 0 0 0-1zm0 2.5a.5.5 0 0 0 0 1h5a.5.5 0 0 0 0-1z" />
    </svg>)
}

const SendIcon = () => {
    return (<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <line x1="22" y1="2" x2="11" y2="13" />
        <polygon points="22 2 15 22 11 13 2 9 22 2" />
    </svg>)
}

const DotsIcon = () => {
    return (<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <circle cx="12" cy="12" r="1" />
        <circle cx="19" cy="12" r="1" />
        <circle cx="5" cy="12" r="1" />
    </svg>)
}

export default ChatBot;